'use client';

import { useEffect } from 'react';

function ensurePointerEventsAuto(el: HTMLElement) {
  if (!el) return;
  const computed = getComputedStyle(el);
  if (computed.pointerEvents === 'none') {
    // Force it back to auto via inline style (overrides classes)
    el.style.pointerEvents = 'auto';
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[PointerEventsGuard] Fixed pointer-events:none on',
        el.tagName.toLowerCase(),
      );
    }
  }
}

export function PointerEventsGuard() {
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;

    // Run once on mount
    ensurePointerEventsAuto(html);
    ensurePointerEventsAuto(body);

    // Observer callback
    const handleMutation: MutationCallback = () => {
      ensurePointerEventsAuto(html);
      ensurePointerEventsAuto(body);
    };

    // Watch for style / class changes that might affect pointer-events
    const observer = new MutationObserver(handleMutation);

    observer.observe(html, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    // Also periodically re-assert as a last resort (e.g. JS sets styles)
    const intervalId = window.setInterval(() => {
      ensurePointerEventsAuto(html);
      ensurePointerEventsAuto(body);
    }, 500);

    return () => {
      observer.disconnect();
      window.clearInterval(intervalId);
    };
  }, []);

  // Renders nothing – purely side effects
  return null;
}
